import { useEffect } from "react";
import { getArticleSchema, getFAQSchema } from "@/lib/structuredData";

export interface PillarFAQ {
  question: string;
  answer: string;
}

export interface PillarSeoProps {
  /** Canonical slug — e.g. "ms-fatigue-tracker" (no leading slash). */
  slug: string;
  title: string;
  description: string;
  faqs: PillarFAQ[];
  /** ISO date for Article schema datePublished. */
  date?: string;
  author?: string;
  image?: string;
  /** Canonical base. Defaults to https://myelinahealth.com. */
  baseUrl?: string;
  /** Optional og:type override. Defaults to "article". */
  ogType?: string;
  /** Alt text for og:image. */
  imageAlt?: string;
  /** If true, emit robots=noindex,follow. */
  noindex?: boolean;
}

const DEFAULT_BASE = "https://myelinahealth.com";
const DEFAULT_OG_IMAGE = "https://myelinahealth.com/og-image-large.png";
const DEFAULT_OG_IMAGE_ALT =
  "Myelina Health — AI energy prediction for people with MS";

const upsertMeta = (key: string, content: string, attr: "name" | "property" = "name") => {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href: string) => {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
};

const upsertJsonLd = (id: string, payload: unknown) => {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

/**
 * Drop-in SEO block for pillar pages.
 * Injects title, description, og:*, canonical, Article JSON-LD, FAQPage JSON-LD.
 * Cleans up its own JSON-LD on unmount so per-route schema doesn't leak across pages.
 */
export const PillarSeo = ({
  slug,
  title,
  description,
  faqs,
  date = new Date().toISOString().slice(0, 10),
  author = "Myelina Health Editorial",
  image,
  baseUrl = DEFAULT_BASE,
  ogType = "article",
  imageAlt,
  noindex = false,
}: PillarSeoProps) => {
  useEffect(() => {
    const canonical = `${baseUrl.replace(/\/$/, "")}/${slug.replace(/^\//, "")}`;
    const articleId = `pillar-jsonld-article-${slug}`;
    const faqId = `pillar-jsonld-faq-${slug}`;

    const previousTitle = document.title;
    document.title = title;

    const resolvedImage = image
      ? image.startsWith("http")
        ? image
        : `${baseUrl.replace(/\/$/, "")}${image.startsWith("/") ? "" : "/"}${image}`
      : DEFAULT_OG_IMAGE;
    const resolvedImageAlt = imageAlt || title;

    upsertMeta("description", description);
    upsertMeta("robots", noindex ? "noindex, follow" : "index, follow");
    upsertMeta("og:site_name", "Myelina Health", "property");
    upsertMeta("og:locale", "en_US", "property");
    upsertMeta("og:title", title, "property");
    upsertMeta("og:description", description, "property");
    upsertMeta("og:url", canonical, "property");
    upsertMeta("og:type", ogType, "property");
    upsertMeta("og:image", resolvedImage, "property");
    upsertMeta("og:image:alt", resolvedImageAlt, "property");
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", title);
    upsertMeta("twitter:description", description);
    upsertMeta("twitter:image", resolvedImage);
    upsertMeta("twitter:image:alt", resolvedImageAlt);
    upsertMeta("author", author);
    upsertCanonical(canonical);

    upsertJsonLd(
      articleId,
      getArticleSchema({ title, description, slug, author, date, image: resolvedImage }),
    );
    if (faqs.length > 0) {
      upsertJsonLd(faqId, getFAQSchema(faqs));
    }

    return () => {
      document.getElementById(articleId)?.remove();
      document.getElementById(faqId)?.remove();
      document.title = previousTitle;
    };
  }, [slug, title, description, faqs, date, author, image, baseUrl, ogType, imageAlt, noindex]);

  return null;
};

export default PillarSeo;
